// Canonical owner-facing skill groups. One ID => one card => exactly one group.
// A shared native skill retains every employee assignment, not duplicate cards.
// This is display classification only; it cannot activate a provider or a skill run.
export const SKILL_GROUPS=[
 'Research',
 'Finance & Markets',
 'Marketing & Sales',
 'Content & Publishing',
 'Branding & Design',
 'Operations & Projects',
 'Engineering & Data',
 'Legal & Compliance',
 'Safety & Quality',
 'Product & Strategy',
 'Agent Systems',
 'Personal & Productivity',
 'Customer Support & Fulfillment',
] as const;
export type SkillGroup=typeof SKILL_GROUPS[number];
type SkillClassification={id:string;name:string;category:string;kind?:'profile'|'imported';preparedRoles?:string[]};

// Exceptions are based on reviewed names where an upstream category is broader
// than the real job. Preserve original source categories separately in metadata.
const exactTitles:Record<string,SkillGroup>={
 'market prices':'Finance & Markets',
 'market health':'Finance & Markets',
 'market signal review':'Finance & Markets',
 'market trends':'Finance & Markets',
 'sports odds':'Research',
 'sports odds calculator':'Research',
 'financial research':'Finance & Markets',
 'investment research journal':'Finance & Markets',
 'market sizing':'Research',
 'cloud cost optimization':'Engineering & Data',
 'startup financial modeling':'Finance & Markets',
 'startup metrics framework':'Finance & Markets',
 'data storytelling':'Engineering & Data',
 'kpi dashboard design':'Engineering & Data',
 'human-sounding content review':'Content & Publishing',
 'linkedin content planning':'Content & Publishing',
 'video content strategy':'Content & Publishing',
 'youtube content optimization':'Content & Publishing',
 'website copywriting':'Content & Publishing',
 'codebase to product requirements':'Product & Strategy',
 'roadmap communication':'Product & Strategy',
 'react native architecture':'Engineering & Data',
 'ios design patterns':'Branding & Design',
 'apple interface guidance':'Branding & Design',
 'ux research and design':'Branding & Design',
 'ui design system planning':'Branding & Design',
 'responsive interface design':'Branding & Design',
 'design system patterns':'Branding & Design',
 'interaction design':'Branding & Design',
 'mcp builder':'Agent Systems',
 'skill creator':'Agent Systems',
 'incident postmortems':'Safety & Quality',
 'incident response runbooks':'Safety & Quality',
 'revenue operations':'Marketing & Sales',
 'customer success':'Customer Support & Fulfillment',
 'customer onboarding':'Customer Support & Fulfillment',
 'financial and opportunity analysis':'Research',
 'financial outcome interpretation':'Finance & Markets',
 'verified payment attribution':'Finance & Markets',
 'financial claim cross-check':'Safety & Quality',
 'source rights discipline':'Legal & Compliance',
 'icon & asset implementation':'Engineering & Data',
 'technical research':'Research',
 'sales prospect research':'Marketing & Sales',
 'popular stream intelligence':'Research',
 'performance-led improvement':'Content & Publishing',
 'agentopia personal outcome orchestration':'Personal & Productivity',
 '7 am executive daily reporting':'Operations & Projects',
 'daily report snapshot reconciliation':'Operations & Projects',
 'opportunity and platform verification':'Research',
 'zero-capital opportunity discovery':'Research',
 'opportunity verification':'Research',
 'zero-cost execution planning':'Operations & Projects',
 'independent execution':'Operations & Projects',
 'authorized capital execution':'Finance & Markets',
 'risk / reward and position sizing':'Finance & Markets',
 'forecasting and probability calibration':'Finance & Markets',
 'open position monitoring':'Finance & Markets',
 'source verification':'Research',
 'finish the loop':'Operations & Projects',
 'runaway pattern detection':'Safety & Quality',
 'safety incident triage':'Safety & Quality',
 'clear owner notification':'Safety & Quality'
};
const nativeRoleGroup:Record<string,SkillGroup>={
 brand_identity:'Branding & Design',
 content_production:'Content & Publishing',
 debt_management:'Finance & Markets',
 engineering_director:'Engineering & Data',
 finance_performance:'Finance & Markets',
 goal_achievement:'Personal & Productivity',
 income_builder:'Marketing & Sales',
 legal_compliance:'Legal & Compliance',
 marketing_distribution:'Marketing & Sales',
 markets_intelligence:'Finance & Markets',
 operations_fulfillment:'Customer Support & Fulfillment',
 personal_life_assistant:'Personal & Productivity',
 praamis_executive:'Agent Systems',
 product_director:'Product & Strategy',
 project_manager:'Operations & Projects',
 publishing_marketplace:'Content & Publishing',
 research_intelligence:'Research',
 revenue_director:'Marketing & Sales',
 runtime_safety_officer:'Safety & Quality',
 sales_director:'Marketing & Sales',
 sentinel_auditor:'Safety & Quality',
 workflow_operations_manager:'Operations & Projects'
};
const sourceGroup:Record<string,SkillGroup>={
 'Agent Systems':'Agent Systems',
 'Analytics':'Engineering & Data',
 'Branding':'Branding & Design',
 'Business':'Product & Strategy',
 'Communications':'Content & Publishing',
 'Community':'Marketing & Sales',
 'Compliance':'Legal & Compliance',
 'Content':'Content & Publishing',
 'Data':'Engineering & Data',
 'Design':'Branding & Design',
 'Engineering':'Engineering & Data',
 'Finance':'Finance & Markets',
 'Finance & Markets':'Finance & Markets',
 'Growth':'Marketing & Sales',
 'Legal':'Legal & Compliance',
 'Legal & Compliance':'Legal & Compliance',
 'Market Data':'Finance & Markets',
 'Market Research':'Research',
 'Marketing':'Marketing & Sales',
 'Marketing & Content':'Marketing & Sales',
 'Operations':'Operations & Projects',
 'Personal & Publishing':'Personal & Productivity',
 'Product':'Product & Strategy',
 'Product & Design':'Product & Strategy',
 'Productivity':'Personal & Productivity',
 'Project':'Operations & Projects',
 'Research':'Research',
 'Safety & Reliability':'Safety & Quality',
 'Sales':'Marketing & Sales',
 'Sports Research':'Research',
 'Strategy & Revenue':'Product & Strategy',
 'Support':'Customer Support & Fulfillment',
 'Technical':'Engineering & Data',
 'Writing':'Content & Publishing'
};
for(const group of SKILL_GROUPS)sourceGroup[group]=group;

// Overlapping human titles are disambiguated by immutable ID and the assigned role.
export function skillGroup(skill:SkillClassification):SkillGroup{
 const title=skill.name.toLowerCase().trim();
 if(skill.id==='global-skill-finish-loop-v2')return 'Operations & Projects';
 if(exactTitles[title])return exactTitles[title];
 if(skill.kind==='profile'){
  const role=skill.preparedRoles?.[0]||'';
  if(role==='markets_intelligence' && /\bsports\b/i.test(title))return 'Research';
  if(role==='legal_compliance')return 'Legal & Compliance';
  return nativeRoleGroup[role]||'Operations & Projects';
 }
 return sourceGroup[skill.category]||'Operations & Projects';
}
export function isSkillGroup(value:string):value is SkillGroup{
 return (SKILL_GROUPS as readonly string[]).includes(value);
}
